import React, { useState, useEffect, useCallback } from 'react';
import StraightChessBoard from './StraightChessBoard';
import GameShareButton from './GameShareButton';
import {
  StraightGameState,
  StraightPiece,
  StraightMove,
  Position,
  Player,
  PlayerColor,
  GameMode,
  Difficulty,
  STRAIGHT_CONFIG,
  STRAIGHT_PIECE_EMOJIS
} from '../types/game';

interface StraightChessGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  playerColor?: PlayerColor; // 玩家选择的颜色，仅在人机对战时使用
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const StraightChessGame: React.FC<StraightChessGameProps> = ({
  gameMode,
  difficulty = 'medium',
  playerColor,
  onBackToMenu,
  onBackToModeSelect
}) => {
  // 确定AI玩家的颜色（如果玩家指定了颜色，AI则使用相反颜色）
  const aiPlayerColor: Player = playerColor === 'black' ? 'white' :
                               playerColor === 'white' ? 'black' : 'white';

  // 直棋的24个有效位置
  const validPositions: Position[] = [
    // 外层正方形
    { row: 0, col: 0 }, { row: 0, col: 3 }, { row: 0, col: 6 },
    { row: 3, col: 0 },                           { row: 3, col: 6 },
    { row: 6, col: 0 }, { row: 6, col: 3 }, { row: 6, col: 6 },

    // 中层正方形
    { row: 1, col: 1 }, { row: 1, col: 3 }, { row: 1, col: 5 },
    { row: 3, col: 1 },                           { row: 3, col: 5 },
    { row: 5, col: 1 }, { row: 5, col: 3 }, { row: 5, col: 5 },

    // 内层正方形
    { row: 2, col: 2 }, { row: 2, col: 3 }, { row: 2, col: 4 },
    { row: 3, col: 2 },                           { row: 3, col: 4 },
    { row: 4, col: 2 }, { row: 4, col: 3 }, { row: 4, col: 4 },
  ];

  // 成直的组合（三子连线）- 直棋规则中的所有有效连线
  const millCombinations: Position[][] = [
    // 外层正方形的边
    [{ row: 0, col: 0 }, { row: 0, col: 3 }, { row: 0, col: 6 }],
    [{ row: 0, col: 6 }, { row: 3, col: 6 }, { row: 6, col: 6 }],
    [{ row: 6, col: 6 }, { row: 6, col: 3 }, { row: 6, col: 0 }],
    [{ row: 6, col: 0 }, { row: 3, col: 0 }, { row: 0, col: 0 }],

    // 中层正方形的边
    [{ row: 1, col: 1 }, { row: 1, col: 3 }, { row: 1, col: 5 }],
    [{ row: 1, col: 5 }, { row: 3, col: 5 }, { row: 5, col: 5 }],
    [{ row: 5, col: 5 }, { row: 5, col: 3 }, { row: 5, col: 1 }],
    [{ row: 5, col: 1 }, { row: 3, col: 1 }, { row: 1, col: 1 }],

    // 内层正方形的边
    [{ row: 2, col: 2 }, { row: 2, col: 3 }, { row: 2, col: 4 }],
    [{ row: 2, col: 4 }, { row: 3, col: 4 }, { row: 4, col: 4 }],
    [{ row: 4, col: 4 }, { row: 4, col: 3 }, { row: 4, col: 2 }],
    [{ row: 4, col: 2 }, { row: 3, col: 2 }, { row: 2, col: 2 }],

    // 中心连接线 - 竖直方向
    [{ row: 0, col: 3 }, { row: 1, col: 3 }, { row: 2, col: 3 }],
    [{ row: 2, col: 3 }, { row: 3, col: 3 }, { row: 4, col: 3 }],
    [{ row: 4, col: 3 }, { row: 5, col: 3 }, { row: 6, col: 3 }],

    // 中心连接线 - 水平方向
    [{ row: 3, col: 0 }, { row: 3, col: 1 }, { row: 3, col: 2 }],
    [{ row: 3, col: 4 }, { row: 3, col: 5 }, { row: 3, col: 6 }],

    // 对角线 - 四角到内层
    [{ row: 0, col: 0 }, { row: 1, col: 1 }, { row: 2, col: 2 }],
    [{ row: 0, col: 6 }, { row: 1, col: 5 }, { row: 2, col: 4 }],
    [{ row: 6, col: 0 }, { row: 5, col: 1 }, { row: 4, col: 2 }],
    [{ row: 6, col: 6 }, { row: 5, col: 5 }, { row: 4, col: 4 }],

    // 内层正方形的对角线
    [{ row: 2, col: 2 }, { row: 3, col: 3 }, { row: 4, col: 4 }],
    [{ row: 2, col: 4 }, { row: 3, col: 3 }, { row: 4, col: 2 }],

    // 中层正方形的对角线
    [{ row: 1, col: 1 }, { row: 3, col: 3 }, { row: 5, col: 5 }],
    [{ row: 1, col: 5 }, { row: 3, col: 3 }, { row: 5, col: 1 }],

    // 从中心向外的对角线延伸
    [{ row: 1, col: 1 }, { row: 2, col: 2 }, { row: 3, col: 3 }],
    [{ row: 1, col: 5 }, { row: 2, col: 4 }, { row: 3, col: 3 }],
    [{ row: 5, col: 1 }, { row: 4, col: 2 }, { row: 3, col: 3 }],
    [{ row: 5, col: 5 }, { row: 4, col: 4 }, { row: 3, col: 3 }],
  ];

  // 调试函数：打印所有成直组合
  const debugMillCombinations = () => {
    console.log('=== 直棋成直组合调试 ===');
    console.log(`总共 ${millCombinations.length} 个成直组合:`);
    millCombinations.forEach((mill, index) => {
      console.log(`成直 ${index + 1}: [${mill.map(p => `(${p.row},${p.col})`).join(', ')}]`);
    });
    console.log('========================');
  };

  // 在开发环境下打印调试信息
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugMillCombinations();
    }
  }, []);

  // 初始化游戏状态
  const initializeGame = useCallback((): StraightGameState => {
    const board: (StraightPiece | null)[][] = Array(7).fill(null).map(() => Array(7).fill(null));

    return {
      board,
      currentPlayer: 'black',
      status: 'placing',
      winner: null,
      moves: [],
      gameHistory: [],
      gameMode,
      gameType: 'straight',
      difficulty,
      isAIThinking: false,

      selectedPiece: undefined,
      possibleMoves: [],
      capturedPieces: { black: [], white: [] },

      phase: 'placing',
      piecesToPlace: 18, // 每人9个棋子
      blackPiecesOnBoard: 0,
      whitePiecesOnBoard: 0,

      lastMill: undefined,
      millFormations: { black: [], white: [] },
      capturedMills: { black: [], white: [] },
      pendingCapture: false,
      canCapture: false,

      totalMoves: 0,
      captureMoves: 0,
      millFormationsCount: 0,

      blackPiecesRemaining: 9,
      whitePiecesRemaining: 9,
    };
  }, [gameMode, difficulty]);

  const [gameState, setGameState] = useState<StraightGameState>(initializeGame);
  const [showRules, setShowRules] = useState<boolean>(false);
  const [canUndo, setCanUndo] = useState(false);

  // 检查是否为有效位置
  const isValidPosition = (row: number, col: number): boolean => {
    return validPositions.some(pos => pos.row === row && pos.col === col);
  };

  // 检查两个位置是否相邻
  const areAdjacent = (from: Position, to: Position): boolean => {
    const connections = [
      // 外层正方形边
      [{ row: 0, col: 0 }, { row: 0, col: 3 }],
      [{ row: 0, col: 3 }, { row: 0, col: 6 }],
      [{ row: 0, col: 6 }, { row: 3, col: 6 }],
      [{ row: 3, col: 6 }, { row: 6, col: 6 }],
      [{ row: 6, col: 6 }, { row: 6, col: 3 }],
      [{ row: 6, col: 3 }, { row: 6, col: 0 }],
      [{ row: 6, col: 0 }, { row: 3, col: 0 }],
      [{ row: 3, col: 0 }, { row: 0, col: 0 }],

      // 中层正方形边
      [{ row: 1, col: 1 }, { row: 1, col: 3 }],
      [{ row: 1, col: 3 }, { row: 1, col: 5 }],
      [{ row: 1, col: 5 }, { row: 3, col: 5 }],
      [{ row: 3, col: 5 }, { row: 5, col: 5 }],
      [{ row: 5, col: 5 }, { row: 5, col: 3 }],
      [{ row: 5, col: 3 }, { row: 5, col: 1 }],
      [{ row: 5, col: 1 }, { row: 3, col: 1 }],
      [{ row: 3, col: 1 }, { row: 1, col: 1 }],

      // 内层正方形边
      [{ row: 2, col: 2 }, { row: 2, col: 3 }],
      [{ row: 2, col: 3 }, { row: 2, col: 4 }],
      [{ row: 2, col: 4 }, { row: 3, col: 4 }],
      [{ row: 3, col: 4 }, { row: 4, col: 4 }],
      [{ row: 4, col: 4 }, { row: 4, col: 3 }],
      [{ row: 4, col: 3 }, { row: 4, col: 2 }],
      [{ row: 4, col: 2 }, { row: 3, col: 2 }],
      [{ row: 3, col: 2 }, { row: 2, col: 2 }],

      // 连接线
      [{ row: 0, col: 3 }, { row: 1, col: 3 }],
      [{ row: 1, col: 3 }, { row: 2, col: 3 }],
      [{ row: 2, col: 3 }, { row: 3, col: 3 }],
      [{ row: 3, col: 3 }, { row: 4, col: 3 }],
      [{ row: 4, col: 3 }, { row: 5, col: 3 }],
      [{ row: 5, col: 3 }, { row: 6, col: 3 }],
      [{ row: 3, col: 0 }, { row: 3, col: 1 }],
      [{ row: 3, col: 1 }, { row: 3, col: 2 }],
      [{ row: 3, col: 4 }, { row: 3, col: 5 }],
      [{ row: 3, col: 5 }, { row: 3, col: 6 }],

      // 对角线连接 - 完整版本
      [{ row: 0, col: 0 }, { row: 1, col: 1 }],
      [{ row: 1, col: 1 }, { row: 2, col: 2 }],
      [{ row: 0, col: 6 }, { row: 1, col: 5 }],
      [{ row: 1, col: 5 }, { row: 2, col: 4 }],
      [{ row: 6, col: 0 }, { row: 5, col: 1 }],
      [{ row: 5, col: 1 }, { row: 4, col: 2 }],
      [{ row: 6, col: 6 }, { row: 5, col: 5 }],
      [{ row: 5, col: 5 }, { row: 4, col: 4 }],

      // 中层正方形的对角线
      [{ row: 1, col: 1 }, { row: 3, col: 3 }],
      [{ row: 3, col: 3 }, { row: 5, col: 5 }],
      [{ row: 1, col: 5 }, { row: 3, col: 3 }],
      [{ row: 3, col: 3 }, { row: 5, col: 1 }],
    ];

    return connections.some(([a, b]) =>
      (a.row === from.row && a.col === from.col && b.row === to.row && b.col === to.col) ||
      (a.row === to.row && a.col === to.col && b.row === from.row && b.col === from.col)
    );
  };

  // 获取位置价值（AI评估用）
  const getPositionValue = (pos: Position): number => {
    // 中心位置价值最高
    if (pos.row === 3 && pos.col === 3) return 25;

    // 内层正方形位置
    if (pos.row >= 2 && pos.row <= 4 && pos.col >= 2 && pos.col <= 4) return 20;

    // 中层正方形位置
    if (pos.row >= 1 && pos.row <= 5 && pos.col >= 1 && pos.col <= 5) return 15;

    // 外层位置
    if (pos.row === 0 || pos.row === 6 || pos.col === 0 || pos.col === 6) return 10;

    return 5;
  };

  // 检查两个成直组合是否相同（用于判断是否已经吃过子）
  const areMillsEqual = (mill1: Position[], mill2: Position[]): boolean => {
    if (mill1.length !== mill2.length) return false;

    const mill1String = mill1.map(pos => `${pos.row},${pos.col}`).sort().join('|');
    const mill2String = mill2.map(pos => `${pos.row},${pos.col}`).sort().join('|');

    return mill1String === mill2String;
  };

  // 检查某个成直组合是否已经被吃过子（所有游戏阶段都有效）
  const isMillAlreadyCaptured = (mill: Position[], gameState: StraightGameState, player: Player): boolean => {
    const capturedMills = player === 'black'
      ? gameState.capturedMills.black
      : gameState.capturedMills.white;

    const millString = mill.map(pos => `${pos.row},${pos.col}`).sort().join('|');

    const alreadyCaptured = capturedMills.some(capturedMill => {
      const capturedMillString = capturedMill.map(pos => `${pos.row},${pos.col}`).sort().join('|');
      return capturedMillString === millString;
    });

    if (alreadyCaptured) {
      console.log('=== 连线已用于吃子，不能重复吃子 ===', {
        player,
        mill: millString,
        capturedMills: capturedMills.map(cm => cm.map(pos => `${pos.row},${pos.col}`).sort().join('|')),
      });
    } else {
      console.log('=== 连线未使用，可以吃子 ===', {
        player,
        mill: millString,
      });
    }

    return alreadyCaptured;
  };

  // 检查是否形成成直且未吃过子
  const checkForMill = (board: (StraightPiece | null)[][], player: Player, gameState?: StraightGameState): Position[] | null => {
    console.log('=== 检查成直 ===', { player, hasGameState: !!gameState });

    for (const combination of millCombinations) {
      if (combination.every(pos =>
        board[pos.row][pos.col]?.player === player
      )) {
        console.log('=== 发现潜在成直 ===', { player, combination });

        // 检查是否已经吃过子（所有阶段都检查）
        if (gameState) {
          if (!isMillAlreadyCaptured(combination, gameState, player)) {
            console.log('=== 成直有效，可以吃子 ===', { player, combination });
            return combination;
          } else {
            console.log('=== 成直已用于吃子，跳过 ===', { player, combination });
          }
        } else {
          // 如果没有提供gameState，不检查重复吃子（用于AI评估等场景）
          console.log('=== 成直检测（AI评估模式） ===', { player, combination });
          return combination;
        }
      }
    }
    console.log('=== 未发现成直 ===', { player });
    return null;
  };

  // 获取可吃的对方棋子
  const getCapturablePieces = (board: (StraightPiece | null)[][], opponent: Player): Position[] => {
    const capturable: Position[] = [];

    for (const pos of validPositions) {
      const piece = board[pos.row][pos.col];
      if (piece && piece.player === opponent) {
        // 检查这个棋子是否在成直中
        const inMill = millCombinations.some(combination =>
          combination.some(p => p.row === pos.row && p.col === pos.col) &&
          combination.every(p => board[p.row][p.col]?.player === opponent)
        );

        // 只有不在成直中的棋子才能被吃（除非对方所有棋子都在成直中）
        if (!inMill) {
          capturable.push(pos);
        }
      }
    }

    // 如果没有可吃的棋子，说明对方所有棋子都在成直中，可以吃任意棋子
    if (capturable.length === 0) {
      for (const pos of validPositions) {
        const piece = board[pos.row][pos.col];
        if (piece && piece.player === opponent) {
          capturable.push(pos);
        }
      }
    }

    return capturable;
  };

  // 获取可能的移动位置
  const getPossibleMoves = (state: StraightGameState): Position[] => {
    if (state.phase === 'placing') {
      // 布子阶段：返回所有空位
      return validPositions.filter(pos => !state.board[pos.row][pos.col]);
    } else if (state.phase === 'moving' || state.phase === 'flying') {
      // 移动阶段
      if (!state.selectedPiece) return [];

      const piece = state.board[state.selectedPiece.row][state.selectedPiece.col];
      if (!piece || piece.player !== state.currentPlayer) return [];

      const possibleMoves: Position[] = [];

      if (state.phase === 'flying' &&
          ((state.currentPlayer === 'black' && state.blackPiecesOnBoard === 3) ||
           (state.currentPlayer === 'white' && state.whitePiecesOnBoard === 3))) {
        // 飞行阶段：可以移动到任意空位
        validPositions.forEach(pos => {
          if (!state.board[pos.row][pos.col]) {
            possibleMoves.push(pos);
          }
        });
      } else {
        // 普通移动：只能移动到相邻的空位
        validPositions.forEach(pos => {
          if (!state.board[pos.row][pos.col] &&
              areAdjacent(state.selectedPiece!, pos)) {
            possibleMoves.push(pos);
          }
        });
      }

      return possibleMoves;
    }

    return [];
  };

  // 执行移动
  const makeMove = (row: number, col: number) => {
    console.log('=== makeMove被调用 ===', { row, col, currentPlayer: gameState.currentPlayer, isAIThinking: gameState.isAIThinking, status: gameState.status });

    if (gameState.status === 'finished') {
      console.log('=== makeMove被阻止：游戏已结束 ===');
      return;
    }

    // 获取最新状态（解决AI移动时selectedPiece不同步的问题）
    const currentState = (() => {
      const state = { ...gameState };

      // 如果这是AI移动且selectedPiece未设置，尝试从可能的缓存中获取
      if (!state.selectedPiece && window.aiSelectedPiece) {
        state.selectedPiece = window.aiSelectedPiece;
        console.log('=== 从缓存恢复AI选择的棋子 ===', window.aiSelectedPiece);
      }

      return state;
    })();

    const newState = { ...currentState };
    const board = newState.board.map(row => [...row]);

    if (newState.phase === 'placing') {
      // 布子阶段
      if (newState.pendingCapture) {
        // 执行吃子
        const piece = board[row][col];
        if (!piece || piece.player === newState.currentPlayer) return;

        // 检查是否可以吃这个棋子
        const capturablePositions = getCapturablePieces(board, piece.player);
        if (!capturablePositions.some(pos => pos.row === row && pos.col === col)) return;

        // 执行吃子
        const capturedPiece = board[row][col]!;
        board[row][col] = null;

        // 更新被吃棋子记录
        if (capturedPiece.player === 'black') {
          newState.capturedPieces.black.push(capturedPiece);
          newState.blackPiecesOnBoard--;
          newState.blackPiecesRemaining--;
        } else {
          newState.capturedPieces.white.push(capturedPiece);
          newState.whitePiecesOnBoard--;
          newState.whitePiecesRemaining--;
        }

        // 记录用于吃子的连线（防止同一连线重复吃子）
        if (newState.lastMill) {
          console.log('=== 记录用于吃子的连线 ===', {
            player: newState.currentPlayer,
            mill: newState.lastMill
          });
          // 注意：此时currentPlayer还是当前玩家，切换玩家前记录
          const playerWhoCaptured = newState.currentPlayer;
          if (playerWhoCaptured === 'black') {
            newState.capturedMills.black.push(newState.lastMill);
          } else {
            newState.capturedMills.white.push(newState.lastMill);
          }
        }

        // 清除待吃子状态
        newState.pendingCapture = false;
        newState.canCapture = false;
        newState.lastMill = undefined;
        newState.currentPlayer = newState.currentPlayer === 'black' ? 'white' : 'black';
        newState.captureMoves++;

        // 检查布子阶段是否结束
        if (newState.piecesToPlace === 0) {
          newState.phase = 'moving';
          newState.status = 'playing';
        }

        // 记录移动
        const move: StraightMove = {
          player: gameState.currentPlayer,
          from: { row, col },
          to: { row, col },
          captures: [capturedPiece],
          isCapture: true,
          timestamp: Date.now()
        };
        newState.moves.push(move);
      } else if (!isValidPosition(row, col) || board[row][col]) {
        return;
      } else {

      // 放置棋子
      const piece: StraightPiece = {
        player: newState.currentPlayer,
        position: { row, col }
      };
      board[row][col] = piece;

      // 更新棋子数量
      if (newState.currentPlayer === 'black') {
        newState.blackPiecesOnBoard++;
        newState.piecesToPlace--;
      } else {
        newState.whitePiecesOnBoard++;
        newState.piecesToPlace--;
      }

      // 检查是否形成成直
      const mill = checkForMill(board, newState.currentPlayer, newState);
      if (mill) {
        newState.lastMill = mill;
        newState.canCapture = true;
        newState.pendingCapture = true;

        // 记录成直
        if (newState.currentPlayer === 'black') {
          newState.millFormations.black.push(mill);
        } else {
          newState.millFormations.white.push(mill);
        }
        newState.millFormationsCount++;
      } else {
        // 没有形成成直，切换玩家
        newState.currentPlayer = newState.currentPlayer === 'black' ? 'white' : 'black';

        // 检查布子阶段是否结束
        if (newState.piecesToPlace === 0) {
          newState.phase = 'moving';
          newState.status = 'playing';
        }
      }

      // 记录移动
      const move: StraightMove = {
        player: gameState.currentPlayer,
        from: { row, col },
        to: { row, col },
        formedMill: !!mill,
        timestamp: Date.now()
      };
      newState.moves.push(move);
      newState.totalMoves++;
      }
    } else if (newState.phase === 'moving' || newState.phase === 'flying') {
      // 移动阶段
      if (newState.pendingCapture) {
        // 执行吃子
        const piece = board[row][col];
        if (!piece || piece.player === newState.currentPlayer) return;

        // 检查是否可以吃这个棋子
        const capturablePositions = getCapturablePieces(board, piece.player);
        if (!capturablePositions.some(pos => pos.row === row && pos.col === col)) return;

        // 执行吃子
        const capturedPiece = board[row][col]!;
        board[row][col] = null;

        // 更新被吃棋子记录
        if (capturedPiece.player === 'black') {
          newState.capturedPieces.black.push(capturedPiece);
          newState.blackPiecesOnBoard--;
          newState.blackPiecesRemaining--;
        } else {
          newState.capturedPieces.white.push(capturedPiece);
          newState.whitePiecesOnBoard--;
          newState.whitePiecesRemaining--;
        }

        // 记录用于吃子的连线（防止同一连线重复吃子）- 移动阶段也需要记录
        if (newState.lastMill) {
          console.log('=== 移动阶段记录用于吃子的连线 ===', {
            player: newState.currentPlayer,
            mill: newState.lastMill
          });
          const playerWhoCaptured = newState.currentPlayer;
          if (playerWhoCaptured === 'black') {
            newState.capturedMills.black.push(newState.lastMill);
          } else {
            newState.capturedMills.white.push(newState.lastMill);
          }
        }

        // 清除待吃子状态
        console.log('=== 吃子完成，切换玩家 ===', {
          currentPlayer: newState.currentPlayer,
          capturedPiece: capturedPiece
        });
        newState.pendingCapture = false;
        newState.canCapture = false;
        newState.lastMill = undefined;
        const oldPlayer = newState.currentPlayer;
        newState.currentPlayer = newState.currentPlayer === 'black' ? 'white' : 'black';
        newState.selectedPiece = undefined;
        newState.captureMoves++;
        console.log('=== 吃子后玩家切换 ===', { from: oldPlayer, to: newState.currentPlayer });

        // 记录移动
        const move: StraightMove = {
          player: gameState.currentPlayer,
          from: { row, col },
          to: { row, col },
          captures: [capturedPiece],
          isCapture: true,
          timestamp: Date.now()
        };
        newState.moves.push(move);

      } else {
        // 移动棋子
        console.log('=== 移动阶段 ===', {
          selectedPiece: newState.selectedPiece,
          targetPos: { row, col },
          currentPlayer: newState.currentPlayer,
          fromPiece: newState.selectedPiece ? board[newState.selectedPiece.row][newState.selectedPiece.col] : null,
          targetOccupied: board[row][col],
          isValidTarget: isValidPosition(row, col)
        });

        if (!newState.selectedPiece) {
          console.log('=== 移动失败：没有选中的棋子 ===');
          return;
        }

        const fromPiece = board[newState.selectedPiece.row][newState.selectedPiece.col];
        if (!fromPiece || fromPiece.player !== newState.currentPlayer) {
          console.log('=== 移动失败：棋子不属于当前玩家 ===', { fromPiece, currentPlayer: newState.currentPlayer });
          return;
        }

        if (!isValidPosition(row, col) || board[row][col]) {
          console.log('=== 移动失败：目标位置无效或被占用 ===', { isValidPosition: isValidPosition(row, col), targetOccupied: board[row][col] });
          return;
        }

        // 执行移动
        const fromPos = newState.selectedPiece;
        board[row][col] = { ...fromPiece, position: { row, col } };
        board[fromPos.row][fromPos.col] = null;

        // 检查是否形成成直
        const mill = checkForMill(board, newState.currentPlayer, newState);
        console.log('=== 移动阶段成直检测 ===', {
          player: newState.currentPlayer,
          fromPos,
          toPos: { row, col },
          formedMill: !!mill,
          millPositions: mill
        });

        if (mill) {
          newState.lastMill = mill;
          newState.canCapture = true;
          newState.pendingCapture = true;

          // 记录成直
          if (newState.currentPlayer === 'black') {
            newState.millFormations.black.push(mill);
            console.log('=== 黑方成直，需要吃子 ===');
          } else {
            newState.millFormations.white.push(mill);
            console.log('=== 白方成直，需要吃子 ===');
          }
          newState.millFormationsCount++;
          console.log('=== 成直后不切换玩家，等待吃子 ===');
        } else {
          // 没有形成成直，切换玩家
          const oldPlayer = newState.currentPlayer;
          newState.currentPlayer = newState.currentPlayer === 'black' ? 'white' : 'black';
          console.log('=== 未形成成直，切换玩家 ===', { from: oldPlayer, to: newState.currentPlayer });
        }

        newState.selectedPiece = undefined;

        // 记录移动
        const move: StraightMove = {
          player: gameState.currentPlayer,
          from: fromPos,
          to: { row, col },
          formedMill: !!mill,
          timestamp: Date.now()
        };
        newState.moves.push(move);
        newState.totalMoves++;
      }
    }

    // 更新可能移动
    newState.possibleMoves = getPossibleMoves(newState);

    // 检查游戏结束条件
    if (newState.blackPiecesRemaining <= 2 || newState.whitePiecesRemaining <= 2) {
      newState.status = 'finished';
      newState.winner = newState.blackPiecesRemaining <= 2 ? 'white' : 'black';
    }

    // 检查是否有玩家无法移动（只在移动阶段）
    if (newState.phase === 'moving' && newState.status === 'playing') {
      const currentPlayerPieces = validPositions.filter(pos =>
        board[pos.row][pos.col]?.player === newState.currentPlayer
      );

      const hasValidMove = currentPlayerPieces.some(piecePos => {
        return validPositions.some(emptyPos =>
          !board[emptyPos.row][emptyPos.col] && areAdjacent(piecePos, emptyPos)
        );
      });

      if (!hasValidMove) {
        newState.status = 'finished';
        newState.winner = newState.currentPlayer === 'black' ? 'white' : 'black';
      }
    }

    // 检查是否进入飞行阶段
    if (newState.phase === 'moving') {
      if ((newState.currentPlayer === 'black' && newState.blackPiecesOnBoard === 3) ||
          (newState.currentPlayer === 'white' && newState.whitePiecesOnBoard === 3)) {
        newState.phase = 'flying';
      }
    }

    newState.board = board;
    newState.gameHistory = [...newState.gameHistory, gameState];
    setGameState(newState);
  };

  // 处理位置选择
  const handleSelectPosition = (row: number, col: number) => {
    if (gameState.isAIThinking || gameState.status === 'finished') return;

    const newState = { ...gameState };

    if (newState.pendingCapture) {
      // 待吃子状态，直接执行吃子
      makeMove(row, col);
    } else if (newState.phase === 'placing') {
      // 布子阶段，直接放置
      makeMove(row, col);
    } else if (newState.phase === 'moving' || newState.phase === 'flying') {
      // 移动阶段
      const piece = newState.board[row][col];

      if (piece && piece.player === newState.currentPlayer) {
        // 选择己方棋子
        newState.selectedPiece = { row, col };
        newState.possibleMoves = getPossibleMoves(newState);
        setGameState(newState);
      } else if (newState.selectedPiece && isPossibleMove(row, col)) {
        // 移动到目标位置
        makeMove(row, col);
      }
    }
  };

  // 检查是否为可能的移动位置
  const isPossibleMove = (row: number, col: number): boolean => {
    return gameState.possibleMoves.some(pos => pos.row === row && pos.col === col);
  };

  
  // AI移动逻辑
  const getAIMove = (state: StraightGameState, player?: Player): Position | null => {
    const aiColor = player || aiPlayerColor;
    console.log('AI getAIMove被调用:', {
      phase: state.phase,
      pendingCapture: state.pendingCapture,
      currentPlayer: state.currentPlayer,
      selectedPiece: state.selectedPiece
    });

    if (state.phase === 'placing') {
      // 布子阶段
      if (state.pendingCapture) {
        // 执行吃子
        const opponent = state.currentPlayer === 'black' ? 'white' : 'black';
        return getBestCaptureMove(state, opponent);
      } else {
        // 选择放置位置
        const validAIMoves = validPositions.filter(pos => !state.board[pos.row][pos.col]);
        console.log('布子阶段可用移动:', validAIMoves.length);
        if (validAIMoves.length === 0) {
          console.log('布子阶段无可用移动');
          return null;
        }

        if (difficulty === 'easy') {
          const result = validAIMoves[Math.floor(Math.random() * validAIMoves.length)];
          console.log('布子阶段AI选择(简单):', result);
          return result;
        } else {
          const result = getBestMove(state, validAIMoves);
          console.log('布子阶段AI选择(智能):', result);
          return result;
        }
      }
    } else if (state.phase === 'moving' || state.phase === 'flying') {
      // 移动阶段
      if (state.pendingCapture) {
        // 执行吃子
        const opponent = state.currentPlayer === 'black' ? 'white' : 'black';
        return getBestCaptureMove(state, opponent);
      } else if (state.selectedPiece) {
        // 已经选择了棋子，现在选择移动位置
        const selectedPiece = state.selectedPiece;
        const validAIMoves: Position[] = [];

        if (state.phase === 'flying' &&
            ((aiColor === 'black' && state.blackPiecesOnBoard === 3) ||
             (aiColor === 'white' && state.whitePiecesOnBoard === 3))) {
          // 飞行阶段：可以移动到任意空位
          validPositions.forEach(pos => {
            if (!state.board[pos.row][pos.col]) {
              validAIMoves.push(pos);
            }
          });
        } else {
          // 普通移动：只能移动到相邻的空位
          validPositions.forEach(pos => {
            if (!state.board[pos.row][pos.col] && areAdjacent(selectedPiece, pos)) {
              validAIMoves.push(pos);
            }
          });
        }

        console.log('移动阶段-移动位置可用选项:', validAIMoves.length);
        if (validAIMoves.length === 0) {
          console.log('移动阶段-移动位置无可用移动');
          return null;
        }

        if (difficulty === 'easy') {
          const result = validAIMoves[Math.floor(Math.random() * validAIMoves.length)];
          console.log('移动阶段AI选择(简单):', result);
          return result;
        } else {
          const result = getBestMove(state, validAIMoves);
          console.log('移动阶段AI选择(智能):', result);
          return result;
        }
      } else {
        // 需要选择一个棋子来移动
        const aiPieces = validPositions.filter(pos =>
          state.board[pos.row][pos.col]?.player === aiColor
        );

        if (aiPieces.length === 0) return null;

        // 评估每个棋子的移动可能性
        const scoredPieces = aiPieces.map(piece => {
          let score = 0;
          let hasValidMoves = false;

          // 检查这个棋子是否有合法移动
          if (state.phase === 'flying' &&
            ((aiColor === 'black' && state.blackPiecesOnBoard === 3) ||
             (aiColor === 'white' && state.whitePiecesOnBoard === 3))) {
            // 飞行阶段
            hasValidMoves = validPositions.some(pos => !state.board[pos.row][pos.col]);
            if (hasValidMoves) {
              score = Math.random() * 10; // 给一些随机性
            }
          } else {
            // 普通移动
            hasValidMoves = validPositions.some(pos =>
              !state.board[pos.row][pos.col] && areAdjacent(piece, pos)
            );
            if (hasValidMoves) {
              score = Math.random() * 10; // 给一些随机性
            }
          }

          return { piece, score, hasValidMoves };
        }).filter(item => item.hasValidMoves);

        console.log('移动阶段-选择棋子阶段:', scoredPieces.length, '个可移动棋子');
        if (scoredPieces.length === 0) {
          console.log('移动阶段-选择棋子阶段无可用棋子');
          return null;
        }

        // 选择最佳棋子
        scoredPieces.sort((a, b) => b.score - a.score);
        const result = scoredPieces[0].piece;
        console.log('移动阶段-选择棋子阶段AI选择:', result);
        return result;
      }
    }

    console.log('AI getAIMove返回: null');
    return null;
  };

  // 获取最佳吃子移动
  const getBestCaptureMove = (state: StraightGameState, opponent: Player): Position | null => {
    const capturablePositions = getCapturablePieces(state.board, opponent);

    if (capturablePositions.length === 0) return null;

    // 优先吃关键位置的棋子
    const scoredMoves = capturablePositions.map(pos => {
      let score = 0;

      // 检查这个位置是否在潜在的成直组合中
      millCombinations.forEach(combination => {
        if (combination.some(p => p.row === pos.row && p.col === pos.col)) {
          const opponentPiecesInComb = combination.filter(p =>
            state.board[p.row][p.col]?.player === opponent
          ).length;
          score += opponentPiecesInComb * 10;
        }
      });

      return { pos, score };
    });

    scoredMoves.sort((a, b) => b.score - a.score);
    return scoredMoves[0]?.pos || null;
  };

  // 获取最佳移动
  const getBestMove = (state: StraightGameState, moves: Position[]): Position | null => {
    const scoredMoves = moves.map(pos => {
      let score = Math.random() * 5; // 基础随机分

      if (state.phase === 'placing') {
        // 布子阶段：评估放置位置
        const board = state.board.map(row => [...row]);
        board[pos.row][pos.col] = { player: 'white', position: pos };

        // 检查是否能形成成直
        const mill = checkForMill(board, 'white', state);
        if (mill) score += 100;

        // 检查是否能阻止对手成直
        board[pos.row][pos.col] = { player: 'black', position: pos };
        const opponentMill = checkForMill(board, 'black', state);
        if (opponentMill) score += 80;

        // 位置价值：中心位置更有价值
        if ((pos.row === 3 && pos.col === 3) ||
            (pos.row >= 1 && pos.row <= 5 && pos.col >= 1 && pos.col <= 5)) {
          score += 20;
        }

      } else if (state.selectedPiece) {
        // 移动阶段：已经有选中的棋子，评估移动位置
        const board = state.board.map(row => [...row]);
        const piece = board[state.selectedPiece.row][state.selectedPiece.col];

        if (piece) {
          board[pos.row][pos.col] = piece;
          board[state.selectedPiece.row][state.selectedPiece.col] = null;

          // 检查是否能形成成直
          const mill = checkForMill(board, 'white', state);
          if (mill) score += 100;

          // 检查是否能阻止对手成直
          board[pos.row][pos.col] = { player: 'black', position: pos };
          const opponentMill = checkForMill(board, 'black', state);
          if (opponentMill) score += 80;
        }

      } else {
        // 移动阶段：评估棋子本身的价值
        const piece = state.board[pos.row][pos.col];
        if (piece && piece.player === aiColor) {
          // 检查这个棋子的灵活性
          let mobility = 0;

          if (state.phase === 'flying' &&
            ((aiColor === 'black' && state.blackPiecesOnBoard === 3) ||
             (aiColor === 'white' && state.whitePiecesOnBoard === 3))) {
            // 飞行阶段：计算可能的移动位置
            mobility = validPositions.filter(p => !state.board[p.row][p.col]).length;
          } else {
            // 普通移动：计算相邻的空位
            mobility = validPositions.filter(p =>
              !state.board[p.row][p.col] && areAdjacent(pos, p)
            ).length;
          }

          score += mobility * 5;

          // 检查是否在成直中（保护重要棋子）
          const inMill = millCombinations.some(combination =>
            combination.some(p => p.row === pos.row && p.col === pos.col) &&
            combination.every(p => state.board[p.row][p.col]?.player === 'white')
          );
          if (inMill) score += 15;
        }
      }

      // 位置价值评分
      score += getPositionValue(pos);

      return { pos, score };
    });

    scoredMoves.sort((a, b) => b.score - a.score);

    // 困难模式选择最佳移动，中等模式有随机性
    if (difficulty === 'hard') {
      return scoredMoves[0]?.pos || null;
    } else {
      // 中等模式：从前3个最佳移动中随机选择
      const topMoves = scoredMoves.slice(0, Math.min(3, scoredMoves.length));
      const selectedMove = topMoves[Math.floor(Math.random() * topMoves.length)];
      return selectedMove?.pos || null;
    }
  };

  // 重新开始游戏
  const resetGame = () => {
    setGameState(initializeGame());
  };

  // 悔棋
  const undoMove = () => {
    if (gameState.gameHistory.length > 0) {
      const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
      setGameState(previousState);
    }
  };

  const getPhaseText = () => {
    if (gameState.pendingCapture) return '选择要吃的棋子';
    switch (gameState.phase) {
      case 'placing': return `布子阶段 (${gameState.piecesToPlace})`;
      case 'moving': return '移动阶段';
      case 'flying': return '飞行阶段 (剩余3枚棋子)';
      default: return '';
    }
  };

  // AI逻辑
  useEffect(() => {
    // 添加调试日志
    if (process.env.NODE_ENV === 'development') {
      console.log('AI useEffect触发:', {
        gameMode,
        currentPlayer: gameState.currentPlayer,
        status: gameState.status,
        isAIThinking: gameState.isAIThinking,
        phase: gameState.phase
      });
    }

    console.log('AI条件检查:', {
      condition1_pvc: gameMode === 'pvc',
      condition2_white: gameState.currentPlayer === 'white',
      condition3_playing: gameState.status === 'playing',
      condition4_notThinking: !gameState.isAIThinking,
      actualStatus: gameState.status,
      actualPlayer: gameState.currentPlayer,
      actualGameMode: gameMode
    });

    if (gameMode === 'pvc' && gameState.currentPlayer === aiPlayerColor &&
        (gameState.status === 'playing' || gameState.status === 'placing') && !gameState.isAIThinking) {

      console.log('AI开始思考...');
      setGameState(prev => ({ ...prev, isAIThinking: true }));

      // AI思考延迟
      const thinkTime = difficulty === 'easy' ? 800 :
                       difficulty === 'medium' ? 1200 : 1800;

      const timer = setTimeout(() => {
        console.log('AI执行移动...');

        // 处理吃子状态
        if (gameState.pendingCapture) {
          console.log('AI执行吃子...');
          const opponent = gameState.currentPlayer === 'black' ? 'white' : 'black';
          console.log('=== AI吃子调试 ===', {
            currentPlayer: gameState.currentPlayer,
            opponent: opponent,
            lastMill: gameState.lastMill,
            capturedMills: gameState.capturedMills
          });

          // 先获取可吃子位置
          const capturablePositions = getCapturablePieces(gameState.board, opponent);
          console.log('=== 可吃子位置 ===', capturablePositions);

          const captureMove = getBestCaptureMove(gameState, opponent);

          if (captureMove) {
            console.log('AI选择吃子:', captureMove);
            // 直接调用makeMove执行吃子
            makeMove(captureMove.row, captureMove.col);
          } else {
            console.log('AI没有找到可吃的棋子');
            // 如果没有可吃的棋子，跳过吃子
            setGameState(prev => ({
              ...prev,
              pendingCapture: false,
              canCapture: false,
              lastMill: undefined,
              isAIThinking: false
            }));
          }
        } else if ((gameState.phase === 'moving' || gameState.phase === 'flying') && !gameState.selectedPiece) {
          console.log('AI移动阶段：选择棋子');
          const piece = getAIMove(gameState);
          console.log('AI选择的棋子:', piece);

          if (piece) {
            // 选择棋子后立即计算移动目标
            const tempState = { ...gameState, selectedPiece: piece };
            const targetMove = getAIMove(tempState);
            console.log('AI计算的移动目标:', targetMove);

            if (targetMove) {
              // 先选择棋子，然后立即移动到目标位置（保持isAIThinking为true防止重复触发）
              // 缓存AI选择的棋子，确保makeMove能获取到
              (window as any).aiSelectedPiece = piece;
              console.log('=== 缓存AI选择的棋子 ===', {
                piece,
                currentPlayer: gameState.currentPlayer,
                pendingCapture: gameState.pendingCapture
              });

              setGameState(prev => ({
                ...prev,
                selectedPiece: piece,
                possibleMoves: getPossibleMoves(tempState),
                isAIThinking: true // 保持AI思考状态
              }));

              // 短暂延迟后执行移动
              setTimeout(() => {
                makeMove(targetMove.row, targetMove.col);
                // makeMove会自动处理状态重置，包括isAIThinking
                // 清除缓存
                delete (window as any).aiSelectedPiece;
                console.log('=== 清除AI棋子缓存 ===');
              }, 500);
              return; // 提前返回，避免重置AI状态
            } else {
              console.log('AI无法找到移动目标');
              setGameState(prev => ({ ...prev, isAIThinking: false }));
            }
          } else {
            console.log('AI无法选择棋子');
            setGameState(prev => ({ ...prev, isAIThinking: false }));
          }
        } else {
          // 布子阶段或已选择棋子的移动阶段
          const move = getAIMove(gameState);
          console.log('AI计算出的移动:', move);
          if (move) {
            // 直接调用移动逻辑
            makeMove(move.row, move.col);
          } else {
            console.log('AI无可用移动');
            // 无可用移动，重置AI状态
            setGameState(prev => ({ ...prev, isAIThinking: false }));
          }
        }
      }, thinkTime);

      return () => clearTimeout(timer);
    }
  }, [gameState.currentPlayer, gameMode, difficulty, gameState.phase, gameState.pendingCapture, gameState.selectedPiece]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking);
  }, [gameState.gameHistory.length, gameState.status, gameState.isAIThinking]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🔘</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent sm:mb-1">
                直棋
              </h1>
              <span className="text-2xl">🔳</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            <button onClick={onBackToMenu} className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium" title="回到首页">
              <span>🏠</span>
              <span className="hidden sm:inline">首页</span>
            </button>
            {/* 返回按钮 */}
            <button onClick={onBackToModeSelect} className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium" title="返回模式选择">
              <span>🔙</span>
              <span className="hidden sm:inline">返回</span>
            </button>

            {/* 重新开始按钮 */}
            <button onClick={resetGame} className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium" title="重新开始">
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button onClick={undoMove} disabled={!canUndo} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              canUndo ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`} title="悔棋">
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button onClick={() => setShowRules(true)} className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium" title="游戏规则">
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="straight" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            <div className={`w-4 h-4 rounded-full ${
              gameState.currentPlayer === 'black' ? 'bg-black' : 'bg-white border-2 border-gray-400'
            }`}></div>
            <span className="text-sm sm:text-base font-medium">
              {gameState.status === 'playing' ? (
                <>
                  {gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合
                  {gameState.isAIThinking && gameMode === 'pvc' && gameState.currentPlayer === aiPlayerColor && (
                    <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
                  )}
                  {gameState.pendingCapture && (
                    <span className="text-xs text-orange-600 animate-pulse ml-2">选择要吃的棋子</span>
                  )}
                </>
              ) : (
                <span className="text-green-600 font-bold text-lg">
                  {gameState.winner ? (gameState.winner === 'black' ? '黑方' : '白方') + '获胜' : (gameState.currentPlayer === 'black' ? '黑方' : '白方') + '回合'}
                </span>
              )}
            </span>
          </div>

          {/* 棋子统计 */}
          <div className="flex items-center space-x-6 text-sm sm:text-base">
            <div className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded-full bg-black`}></div>
              <span>{gameState.blackPiecesOnBoard}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className={`w-3 h-3 rounded-full bg-white border border-gray-400`}></div>
              <span>{gameState.whitePiecesOnBoard}</span>
            </div>
          </div>

          {/* 游戏统计 */}
          <div className="flex items-center space-x-4 text-xs text-gray-600">
            <span>步数: {gameState.totalMoves}</span>
            <span>吃子: {gameState.captureMoves}</span>
            <span>成直: {gameState.millFormationsCount}</span>
          </div>
        </div>
      </div>

      {/* 棋盘 - 响应式居中布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full flex items-center justify-center">
          <StraightChessBoard
            board={gameState.board}
            selectedPiece={gameState.selectedPiece}
            possibleMoves={gameState.possibleMoves}
            lastMill={gameState.lastMill}
            onSelectPosition={handleSelectPosition}
            isAIThinking={gameState.isAIThinking}
          />
        </div>

      </div>
      <div className="mt-10 mb-10 text-center">{gameState.pendingCapture && <span className="ml-2 text-orange-600">🎯 成直！选择要吃的对方棋子</span>}&nbsp;</div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-cyan-50 border-t border-cyan-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-cyan-800 text-center leading-relaxed">
          <strong>直棋规则：</strong>3个同心正方形+4条对角线，共24个交叉点 |
          布子阶段轮流放置9枚棋子，三子成直可吃对方1子 |
          移动阶段沿线移动，剩余3枚棋子时可任意移动 |
          使对方剩余≤2枚棋子或无法移动时获胜
        </p>
      </div>

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800 pr-8">
              ⭕ 直棋游戏规则
            </h2>

            <div className="text-gray-700 space-y-4 mb-6">
              <div>
                <h3 className="font-bold text-lg mb-2">游戏目标</h3>
                <p>将对方棋子减少到≤2枚，或使对方无法移动获胜！</p>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">棋盘设置</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>3个同心正方形+4条对角线，共24个交叉点</li>
                  <li>每位玩家有9枚棋子</li>
                  <li>游戏分为三个阶段：布子阶段、移动阶段、飞行阶段</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">游戏阶段</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>布子阶段：</strong>双方轮流放置9枚棋子到空位上</li>
                  <li><strong>移动阶段：</strong>沿线移动棋子到相邻空位</li>
                  <li><strong>飞行阶段：</strong>当一方只剩3枚棋子时，可移动到任意空位</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">成直规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>当己方3枚棋子在一条直线上时形成"成直"</li>
                  <li>成直后可以吃掉对方任意1枚棋子</li>
                  <li>不在成直中的棋子优先被吃</li>
                  <li>如果对方所有棋子都在成直中，则可以吃任意棋子</li>
                  <li>同一连线不能重复用于吃子</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">移动规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>移动阶段：</strong>只能沿相邻的连线移动一格</li>
                  <li><strong>飞行阶段：</strong>可以移动到任意空位</li>
                  <li>不能移动到已有棋子的位置</li>
                  <li>必须沿线移动，不能跳跃</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">获胜条件</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>将对方棋子数量减少到≤2枚</li>
                  <li>使对方无法进行任何合法移动</li>
                  <li>对方主动认输</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">策略提示</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>布子阶段优先占据中心位置和关键点</li>
                  <li>尽量形成多个潜在的成直线</li>
                  <li>阻止对手形成成直，必要时破坏连线</li>
                  <li>保护自己的棋子，避免被大量吃掉</li>
                  <li>飞行阶段要充分利用移动优势</li>
                  <li>控制关键位置，限制对手移动空间</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowRules(false)}
              className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StraightChessGame;